import { Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { getFutureAndGeneratedInvoicesQueryKey } from '../../../api/InvoiceApi';
import PolicyApi, { getUsePolicyQueryKey } from '../../../api/PolicyApi';
import { getInvoicesForPolicyholderQueryKey } from '../../../api/PolicyholderApi';
import { AuthData } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import DropdownButton from '../../DropdownButton';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import { successToast } from '../../Toast';
import ActionConfirmationModal from '../ActionConfirmationModal';

interface Props {
	policyData: PolicyData;
	authData: AuthData;
	errorData: ErrorData;
}

const AcceptedQuoteActions = (props: Props) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const methods = useFormContext();
	const reset = methods?.reset;
	const { authData, policyData, errorData } = props;
	const { hasPermission } = authData;
	const { selectedQuote, setLoading, setQuoteUpdateObj, policy } =
		props.policyData;

	const refetchData = () => {
		const policyQueryKey = getUsePolicyQueryKey(
			policy.locator,
			authData?.auth?.authorizationToken,
		);
		queryClient.invalidateQueries(policyQueryKey);
		queryClient.invalidateQueries(
			getInvoicesForPolicyholderQueryKey(
				authData?.auth?.authorizationToken,
				policy.policyholderLocator,
			),
		);
		queryClient.invalidateQueries(
			getFutureAndGeneratedInvoicesQueryKey(
				policy.locator,
				authData?.auth?.authorizationToken,
			),
		);
	};

	const onIssue = async () => {
		setLoading(true);
		try {
			await PolicyApi.issuePolicy(
				authData.auth.authorizationToken,
				policy.locator,
			);
			await policyData.loadData(policy.locator);
			successToast('Successfully issued policy');
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to issue policy');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const createNewQuote = async () => {
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
		} = props;
		try {
			const newQuote = await PolicyApi.createNewQuote(
				authorizationToken,
				selectedQuote.locator,
			);
			setQuoteUpdateObj({}); // reset update obj
			await PolicyApi.selectQuote(authorizationToken, newQuote.data.locator);
			const updatedPolicyData = await policyData.loadData(policy.locator);
			const defaultPolicyValues = getDefaultPolicyValues(
				updatedPolicyData.fields,
				props.authData,
			);
			reset(defaultPolicyValues);
			successToast('Successfully created new quote');
			navigate(`/policy/${policy.locator}/quotes/${newQuote.data.locator}`);
			await policyData.loadData(policy.locator);
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to create new quote');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const invalidateQuote = async () => {
		setLoading(true);
		const {
			authData: {
				auth: { authorizationToken },
			},
		} = props;
		try {
			await PolicyApi.invalidateQuote(
				authorizationToken,
				selectedQuote.locator,
			);
			const updatedPolicyData = await policyData.loadData(policy.locator);
			const defaultPolicyValues = getDefaultPolicyValues(
				updatedPolicyData.fields,
				props.authData,
			);
			reset(defaultPolicyValues);
			refetchData();
			successToast('Successfully invalidated quote');
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to invalidate quote');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
		setLoading(false);
	};

	const hiddenOptions = [];

	hasPermission('quote.create') &&
		hiddenOptions.push({ label: 'Create New Quote', callback: createNewQuote });
	hasPermission('quote.invalidate') &&
		hiddenOptions.push({
			label: 'Invalidate Quote',
			callback: invalidateQuote,
		});

	return (
		<Stack direction='row' spacing={1}>
			{hasPermission('policy.issue') && (
				<ActionConfirmationModal
					label='Issue Policy'
					message='Are you sure you want to issue this quote?'
					confirmText='Issue'
					onConfirm={onIssue}
				/>
			)}
			<DropdownButton actions={hiddenOptions} />
		</Stack>
	);
};

export default withPolicyContext<Props>(AcceptedQuoteActions);
